import { ChangeEvent, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import axios, { AxiosResponse } from 'axios';
import { MuiFileInput } from 'mui-file-input';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Resource, TranscodingStatus } from '@esg/esg-global-types';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MetadataError } from '@ep/error-handling';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  Drawer,
  FormControlLabel,
  Switch,
  TextField,
  Theme,
  Tooltip,
  useTheme
} from '@mui/material';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import { generateUuidV4 } from '../../../util/uuid';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { log } from '../../../util/log';
import {
  getResourceFileTypeByExtension,
  resource_label,
  transcodeVideoResource,
  uploadVideoResourceDirectToGcs
} from '../../../lib/resource_centre/resources';
import {
  createResource,
  getResources,
  softDeleteResource,
  updateResource
} from '../../../graphql/services/resource.service';
import {
  CreateResourceClientData,
  SoftDeleteResourceClientData,
  UpdateResourceClientData
} from '@esg/syntiro-api-core';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import ResourceCategorySelect from '../../shared/input/select/ResourceCategorySelect';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { generateFileUploadRequest } from '../../../lib/file_upload/uploadRequestPrep';
import { API_ENDPOINT, GCP_RESOURCE_UPLOAD_BUCKET } from '../../../env';
import TagInput from '../../shared/input/TagInput';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import {
  hasBannedCharacters,
  validateDuplicateEntries
} from '../../../lib/validation/text_validation';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { getAuthHeaderForUser, handleAuthError } from '../../../lib/auth/auth.util';

const FILE_SIZE_LIMIT = 25;
const FILE_SIZE_LIMIT_MESSAGE = `File size exceeds ${FILE_SIZE_LIMIT} Megabytes, please use a smaller file`;
const APP_ENGINE_REQUEST_LIMIT_BYTES = 32 * 1024 * 1024; // 32 MB — files over this go direct to GCS
const SOURCE = 'PanelResourceCentre.tsx';

const ALLOWED_FILE_TYPES: Record<string, Array<string>> = {
  imaging: ['.jpg', '.png', '.webp', '.gif'],
  documents: ['.xls', '.xlsx', '.doc', '.docx', '.pdf', '.ppt', '.pptx', '.rtf'],
  audio: ['.mp3', '.wav', '.aac', '.flac'],
  video: ['.mp4', '.avi'],
  text: ['.csv', '.txt', '.html']
};

/**
 * Datagrid handling the display of configured or Master List Countries
 * @returns {JSX.Element}
 */
const PanelResourceCentre = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [resourceFile, setResourceFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [resourceRows, setResourceRows] = useState<Array<Resource>>([]);
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const [actionRow, setActionRow] = useState<Resource | null>(null);
  const [isCreateWidget, setIsCreateWidget] = useState<boolean>(true);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resourceName, setResourceName] = useState<string>('');
  const [originalResourceName, setOriginalResourceName] = useState<string>('');
  const [validResourceName, setValidResourceName] = useState<boolean>(false);
  const [resourceNameError, setResourceNameError] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<Set<string>>(new Set());
  const [allowDownload, setAllowDownload] = useState<boolean>(true);
  const theme: Theme = useTheme();

  // Handler functions

  // Checks to see if resource name already exists in database.
  const notDuplicateResource = (input_name: unknown): boolean => {
    if (typeof input_name !== 'string') return false;

    if (
      originalResourceName?.length > 0 &&
      validateDuplicateEntries(originalResourceName, input_name)
    ) {
      return true;
    }

    return (
      resourceRows.findIndex(
        (resource) => resource.name && validateDuplicateEntries(resource.name, input_name)
      ) === -1
    );
  };

  const handleResourceFileChange = (new_file: File | null): void => {
    setResourceFile(new_file);
    if (new_file === null) return;
  };

  const handleThumbnailFileChange = (new_file: File | null): void => {
    setThumbnailFile(new_file);
    if (new_file === null) return;
  };

  const handleCreateClick = (): void => {
    setActionRow(null);
    setIsCreateWidget(true);
    setAllowDownload(true);
    setResourceName('');
    setResourceNameError('');
    setDisplayWidgetPanelRight(true);
  };

  const handleEditClick = (resource: Resource): void => {
    setActionRow(resource);
    setOriginalResourceName(resource.name.slice(0));
    setResourceName(resource.name);
    setAllowDownload(resource.allow_download !== false);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleDeleteClick = async (row: Resource): Promise<void> => {
    try {
      setActionRow(row);
      setShowDeleteModal(true);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleDeleteClick'),
          {
            group: group,
            company: company,
            row: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete resource. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleUpdateRows = (rows_data: Array<Resource>): void => {
    setResourceRows(rows_data);
  };

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
  };

  // Input factory to handle resource categories input.
  const categoriesInputFactory = (
    handleInputChange: (input_id: string, value: Array<string> | string | null) => void
  ): JSX.Element => {
    return (
      <ResourceCategorySelect
        multi_select={true}
        selected_options={actionRow?.categories}
        handleChangeCategories={(categories: Array<string> | string | null) => {
          handleInputChange('categories', categories);
        }}
      />
    );
  };

  // Input factory to handle resource name input.
  const resourceNameInputFactory = (
    handleInputChange: (input_id: string, value: string | null) => void
  ): JSX.Element => {
    return (
      <TextField
        error={!validResourceName}
        fullWidth
        id={'name'}
        label={'Name'}
        value={resourceName}
        variant="standard"
        helperText={resourceNameError}
        onChange={(event: ChangeEvent<HTMLInputElement>): void => {
          setResourceName(event.target.value);
          handleInputChange('name', event.target.value);
        }}
      />
    );
  };

  // Input factory to handle custom tags inputs for resource.
  const tagsInputFactory = (
    handleInputChange: (input_id: string, value: Array<string> | null) => void
  ): JSX.Element => {
    return (
      <TagInput
        tag_suggestions={availableTags}
        options={actionRow?.tags ?? []}
        handleChangeTags={(tags: Array<string>) =>
          handleInputChange(
            'tags',
            tags.map((tag: string) => tag.trim())
          )
        }
      ></TagInput>
    );
  };

  // Input factory for the allow_download toggle.
  const allowDownloadInputFactory = (
    handleInputChange: (input_id: string, value: boolean) => void
  ): JSX.Element => (
    <FormControlLabel
      control={
        <Switch
          checked={allowDownload}
          onChange={(e) => {
            setAllowDownload(e.target.checked);
            handleInputChange('allow_download', e.target.checked);
          }}
        />
      }
      label="Allow Download"
    />
  );

  // Input factory to handle resource file uploading.
  const resourceUploadInputFactory = (
    handleInputChange: (input_id: string, value: File | null) => void
  ): JSX.Element => {
    return (
      <MuiFileInput
        value={resourceFile}
        onChange={(resource_file: File | null) => {
          const accepted_extension = [
            ...ALLOWED_FILE_TYPES.audio,
            ...ALLOWED_FILE_TYPES.documents,
            ...ALLOWED_FILE_TYPES.imaging,
            ...ALLOWED_FILE_TYPES.text,
            ...ALLOWED_FILE_TYPES.video
          ];
          const file_parts: Array<string> | undefined = resource_file?.name.split('.');
          const file_extension: string | null = file_parts
            ? `.${file_parts[file_parts?.length - 1]}`
            : null;
          const is_video_file: boolean =
            file_extension !== null && ALLOWED_FILE_TYPES.video.includes(file_extension);
          if (
            resource_file &&
            !is_video_file &&
            resource_file.size > FILE_SIZE_LIMIT * 1024 * 1024
          ) {
            setFeedbackData({
              message: FILE_SIZE_LIMIT_MESSAGE,
              state: true,
              type: 'error'
            });
          } else if (
            resource_file &&
            file_extension &&
            !accepted_extension.includes(file_extension)
          ) {
            setFeedbackData({
              message: 'Not a valid resource file type, please try again!',
              state: true,
              type: 'error'
            });
          } else {
            handleInputChange('resource_upload', resource_file);
            handleResourceFileChange(resource_file);
          }
        }}
        title="Choose resource file"
        label="Resource File"
        inputProps={{
          accept: [
            ...ALLOWED_FILE_TYPES.audio,
            ...ALLOWED_FILE_TYPES.documents,
            ...ALLOWED_FILE_TYPES.imaging,
            ...ALLOWED_FILE_TYPES.text,
            ...ALLOWED_FILE_TYPES.video
          ]
        }}
      />
    );
  };

  // Input factory to handle thumbnail file uploading.
  const thumbnailUploadInputFactory = (
    handleInputChange: (input_id: string, value: File | null) => void
  ): JSX.Element => {
    return (
      <MuiFileInput
        value={thumbnailFile}
        onChange={(thumbnail_file: File | null) => {
          if (thumbnail_file && thumbnail_file.size > FILE_SIZE_LIMIT * 1024 * 1024) {
            setFeedbackData({
              message: FILE_SIZE_LIMIT_MESSAGE,
              state: true,
              type: 'error'
            });
          } else if (!thumbnail_file?.type.includes('image') && thumbnail_file !== null) {
            setFeedbackData({
              message: 'Not a valid image type chosen for thumbnail, please try again!',
              state: true,
              type: 'error'
            });
          } else {
            handleInputChange('thumbnail_upload', thumbnail_file);
            handleThumbnailFileChange(thumbnail_file);
          }
        }}
        title="Choose Thumbail for resource"
        label="Thumbnail"
        inputProps={{ accept: [...ALLOWED_FILE_TYPES.imaging] }}
      />
    );
  };

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'categories',
      headerName: 'Categories',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'type',
      headerName: 'Type',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'allow_download',
      headerName: 'Downloadable',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        return params.row.allow_download !== false ? 'Yes' : 'No';
      }
    },
    {
      field: 'transcoding_status',
      headerName: 'Transcoding Status',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        if (params.row.type !== 'Video' || params.row.allow_download === true) return '-';
        return params.row.transcoding_status ?? '-';
      }
    },
    {
      field: 'created',
      headerName: 'Created',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        return params.row.created ? moment(params.row.created).format('YYYY-MM-DD HH:mm') : '';
      }
    },
    {
      field: 'modified',
      headerName: 'Modified',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        return params.row.modified ? moment(params.row.modified).format('YYYY-MM-DD HH:mm') : '';
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            {
              <GridActionsCellItem
                key={1}
                icon={
                  <Tooltip title="Edit Resource">
                    <EditIcon width={25} fill={theme.palette.primary.main} />
                  </Tooltip>
                }
                size="large"
                label="Edit"
                sx={{
                  color: 'primary.main'
                }}
                onClick={() => handleEditClick(row)}
                disabled={row.locked}
              />
            }
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Resource">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_inputs: CreateInput[] = [
    {
      id: 'allow_download',
      type: 'node',
      label: 'Allow Download',
      optional: true,
      inputNodeFactory: allowDownloadInputFactory
    },
    {
      id: 'resource_upload',
      type: 'node',
      label: 'Resource File',
      inputNodeFactory: resourceUploadInputFactory
    },
    {
      id: 'thumbnail_upload',
      type: 'node',
      label: 'Thumbnail',
      inputNodeFactory: thumbnailUploadInputFactory
    },
    {
      id: 'name',
      type: 'node',
      label: 'Name',
      inputNodeFactory: resourceNameInputFactory,
      textValidation: notDuplicateResource
    },
    {
      id: 'source',
      type: 'text',
      label: 'Source',
      optional: true
    },
    {
      id: 'categories',
      type: 'node',
      label: 'Categories',
      inputNodeFactory: categoriesInputFactory,
      optional: true
    },
    {
      id: 'tags',
      type: 'node',
      label: 'Tags',
      inputNodeFactory: tagsInputFactory
    },
    {
      id: 'description',
      type: 'text',
      label: 'Description',
      optional: true,
      ignoreBannedCharacters: true
    },
    {
      id: 'alias',
      type: 'text',
      label: 'Alias',
      optional: true,
      ignoreBannedCharacters: true,
      helperText:
        'Used to reference key Resources from other application modules. Ask development team for required format.'
    }
  ];

  const edit_inputs: EditInput[] = [
    {
      id: 'allow_download',
      type: 'node',
      label: 'Allow Download',
      optional: true,
      inputNodeFactory: allowDownloadInputFactory
    },
    {
      id: 'resource_upload',
      type: 'node',
      label: 'Resource File',
      inputNodeFactory: resourceUploadInputFactory,
      optional: true
    },
    {
      id: 'thumbnail_upload',
      type: 'node',
      label: 'Thumbnail',
      inputNodeFactory: thumbnailUploadInputFactory,
      optional: true
    },
    {
      id: 'name',
      type: 'node',
      label: 'Name',
      inputNodeFactory: resourceNameInputFactory,
      textValidation: notDuplicateResource
    },
    {
      id: 'source',
      type: 'text',
      label: 'Source',
      optional: true
    },
    {
      id: 'categories',
      type: 'node',
      label: 'Categories',
      inputNodeFactory: categoriesInputFactory,
      optional: true
    },
    {
      id: 'tags',
      type: 'node',
      label: 'Tags',
      inputNodeFactory: tagsInputFactory
    },
    {
      id: 'description',
      type: 'text',
      label: 'Description',
      optional: true,
      ignoreBannedCharacters: true
    },
    {
      id: 'alias',
      type: 'text',
      label: 'Alias',
      optional: true,
      ignoreBannedCharacters: true,
      helperText:
        'Used to reference key Resources from other application modules. Ask development team for required format.'
    }
  ];

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const resources: Array<Resource> = await getResources();
      const available_resource_tags: Array<string> = resources.reduce(
        (available_tag_array: Array<string>, resource: Resource) => [
          ...available_tag_array,
          ...(resource.tags ?? [])
        ],
        []
      );
      const unique_resource_tags: Set<string> = new Set();
      available_resource_tags.forEach((tag: string) => unique_resource_tags.add(tag));
      // Load rows to memory.
      setAvailableTags(unique_resource_tags);
      setResourceRows(resources);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch resources. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  // Handles custom error message replacement on resource name helper text.
  useEffect(() => {
    if (hasBannedCharacters(resourceName)) {
      setResourceNameError('Invalid characters in name');
      setValidResourceName(false);
    } else if (!notDuplicateResource(resourceName)) {
      setResourceNameError('Duplicate resource name');
      setValidResourceName(false);
    } else {
      setValidResourceName(true);
      setResourceNameError('');
    }
  }, [resourceName]);

  // Handles fetching of resources for table from firestore.
  useEffect(() => {
    try {
      (async () => {
        setResourceRows([]);
        fetchRows();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, []);

  const createRow = async (
    allow_download: boolean,
    resource_upload: File,
    thumbnail_upload: File,
    name: string,
    source: string,
    categories: Array<string>,
    tags: Array<string>,
    description: string,
    alias: string | undefined
  ): Promise<void> => {
    try {
      const file_extension = `.${resource_upload.name.split('.').pop()}`;
      const file_type: string = getResourceFileTypeByExtension(file_extension);
      const is_video: boolean = file_type === 'Video';
      const should_transcode_video: boolean = is_video && !allowDownload;

      let resource_upload_url: string;
      if (resource_upload.size > APP_ENGINE_REQUEST_LIMIT_BYTES) {
        resource_upload_url = await uploadVideoResourceDirectToGcs(name, resource_upload);
      } else {
        const resource_file_upload: FormData = generateFileUploadRequest(
          resource_upload,
          resource_upload.name,
          GCP_RESOURCE_UPLOAD_BUCKET,
          name,
          'false',
          is_video ? 'false' : 'true'
        );
        const resource_upload_response: AxiosResponse = await axios({
          method: 'post',
          url: `${API_ENDPOINT}/upload_file`,
          data: resource_file_upload,
          headers: {
            ...(await getAuthHeaderForUser())
          }
        });
        resource_upload_url = resource_upload_response.data.url;
      }

      const thumbnail_file_upload: FormData = generateFileUploadRequest(
        thumbnail_upload,
        thumbnail_upload.name,
        GCP_RESOURCE_UPLOAD_BUCKET,
        name,
        'true',
        'true'
      );
      const thumbnail_upload_response: AxiosResponse = await axios({
        method: 'post',
        url: `${API_ENDPOINT}/upload_file`,
        data: thumbnail_file_upload,
        headers: {
          ...(await getAuthHeaderForUser())
        }
      });

      const create_resource_client_data: CreateResourceClientData = {
        uri: resource_upload_url,
        thumbnail_uri: thumbnail_upload_response.data.url.replace(
          `https://storage.googleapis.com/${GCP_RESOURCE_UPLOAD_BUCKET}/`,
          ''
        ),
        name: name,
        tags: tags ?? [],
        categories: categories ?? [],
        source: source,
        description: description,
        file_extension: file_extension,
        type: file_type,
        size: String(resource_upload.size),
        alias: alias?.toLocaleLowerCase(),
        allow_download,
        ...(should_transcode_video && { transcoding_status: 'pending' as TranscodingStatus })
      };
      const new_resource: Resource | null | undefined = await createResource(
        { group_id: group?.id ?? '', company_id: company?.id ?? '' },
        create_resource_client_data,
        { return_data: true }
      );
      if (new_resource) handleUpdateRows([...resourceRows, new_resource]);

      // Use Backend API endpoint to initiate transcoding job for non-downloadable video resources.
      if (should_transcode_video && new_resource) {
        await transcodeVideoResource(new_resource.id, resource_upload_url);
      }
      return;
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRow'),
          {
            resource_upload: resource_upload,
            thumbnail_upload: thumbnail_upload,
            name: name,
            source: source,
            categories: categories,
            tags: tags,
            description: description
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create new resource. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      handleAuthError(err);
    }
  };

  const updateRow = async (updated_row: Resource, original_data: Resource): Promise<void> => {
    try {
      if (resourceFile) {
        const file_extension = `.${resourceFile.name.split('.').pop()}`;
        const file_type: string = getResourceFileTypeByExtension(file_extension);
        if (resourceFile.size > APP_ENGINE_REQUEST_LIMIT_BYTES) {
          updated_row.uri = await uploadVideoResourceDirectToGcs(updated_row.name, resourceFile);
        } else {
          const resource_file_upload: FormData = generateFileUploadRequest(
            resourceFile,
            resourceFile.name,
            GCP_RESOURCE_UPLOAD_BUCKET,
            updated_row.name,
            'false',
            file_type === 'Video' ? 'false' : 'true'
          );
          const resource_upload_response: AxiosResponse = await axios({
            method: 'post',
            url: `${API_ENDPOINT}/upload_file`,
            data: resource_file_upload,
            headers: {
              ...(await getAuthHeaderForUser())
            }
          });
          updated_row.uri = resource_upload_response.data.url;
        }
        updated_row.file_extension = file_extension;
        updated_row.type = file_type;
      }
      const should_retranscode: boolean =
        !!resourceFile && updated_row.type === 'Video' && updated_row.allow_download !== true;
      if (thumbnailFile) {
        const thumbnail_file_upload = generateFileUploadRequest(
          thumbnailFile,
          thumbnailFile.name,
          GCP_RESOURCE_UPLOAD_BUCKET,
          updated_row.name,
          'true',
          'true'
        );
        const thumbnail_upload_response: AxiosResponse = await axios({
          method: 'post',
          url: `${API_ENDPOINT}/upload_file`,
          data: thumbnail_file_upload,
          headers: {
            ...(await getAuthHeaderForUser())
          }
        });
        updated_row.thumbnail_uri = thumbnail_upload_response.data.url.replace(
          `https://storage.googleapis.com/${GCP_RESOURCE_UPLOAD_BUCKET}/`,
          ''
        );
      }
      const update_resource_client_data: UpdateResourceClientData = {
        id: updated_row.id,
        uri: updated_row.uri,
        thumbnail_uri: updated_row.thumbnail_uri,
        name: updated_row.name,
        tags: updated_row.tags,
        categories: updated_row.categories,
        source: updated_row.source,
        description: updated_row.description,
        type: updated_row.type,
        size: updated_row.size,
        file_extension: updated_row.file_extension,
        alias: updated_row.alias?.toLocaleLowerCase(),
        allow_download: updated_row.allow_download,
        ...(should_retranscode && { transcoding_status: 'pending' as TranscodingStatus })
      };
      const updated_resource_document: Resource | null | undefined = await updateResource(
        { group_id: group?.id ?? '', company_id: company?.id ?? '' },
        update_resource_client_data,
        { return_data: true }
      );
      if (updated_resource_document) {
        handleUpdateRows(
          resourceRows.map((resource: Resource) =>
            updated_row.id === resource.id ? { ...updated_resource_document } : resource
          )
        );
      }
      if (should_retranscode && updated_resource_document) {
        await transcodeVideoResource(updated_resource_document.id, updated_row.uri);
      }
      return;
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update resource. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      handleAuthError(err);
    }
  };

  const deleteRow = async (resource: Resource): Promise<void> => {
    try {
      const soft_delete_resource_client_data: SoftDeleteResourceClientData = { id: resource.id };
      await softDeleteResource(
        { group_id: group?.id ?? '', company_id: company?.id ?? '' },
        soft_delete_resource_client_data
      );
      setResourceRows(
        resourceRows.filter((row: Resource) => {
          return row.id !== resource.id;
        })
      );
      return;
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            resource: resource
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete ${resource.name}. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  {
    /* Interactive Data Table */
  }
  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        allow_delete={true}
        allow_archive={false}
        open={showDeleteModal}
        handleCloseDeleteModal={handleCloseDeleteModal}
        item_type={'Metric'}
        delete_warning={'You will be required to upload this Resource again if deleted'}
        delete_label={actionRow && `${actionRow.name}`}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
      />
      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelRight}
        onClose={() => {
          setDisplayWidgetPanelRight(false);
          setResourceFile(null);
          setThumbnailFile(null);
          setResourceNameError('');
          setResourceName('');
        }}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        {/* Create / Edit Widget */}
        {isCreateWidget ? (
          <>
            <ConfigAddWidget
              widget_label={resource_label.one}
              icon={
                <SchoolOutlinedIcon color="primary" sx={{ marginRight: '2rem' }} fontSize="large" />
              }
              inputs={create_inputs}
              handleClose={() => {
                setDisplayWidgetPanelRight(false);
                setResourceFile(null);
                setThumbnailFile(null);
              }}
              createFunction={async (
                allow_download: boolean,
                resource_upload: File,
                thumbnail_upload: File,
                name: string,
                source: string,
                categories: Array<string>,
                tags: Array<string>,
                description: string,
                alias: string | undefined
              ) => {
                await createRow(
                  allow_download,
                  resource_upload,
                  thumbnail_upload,
                  name,
                  source,
                  categories,
                  tags,
                  description,
                  alias
                );
              }}
            />
          </>
        ) : (
          <ConfigEditWidget
            widget_label={resource_label.one}
            icon={
              <SchoolOutlinedIcon color="primary" sx={{ marginRight: '2rem' }} fontSize="large" />
            }
            handleClose={() => {
              setDisplayWidgetPanelRight(false);
              setResourceFile(null);
              setThumbnailFile(null);
            }}
            edit_entity={actionRow}
            inputs={edit_inputs}
            handleEditInput={(input_id: string, value: unknown) => {
              actionRow && setActionRow({ ...actionRow, [input_id]: value });
            }}
            confirmEditFunction={(updated_resource: Resource, original_resource: Resource) =>
              updateRow(updated_resource, original_resource)
            }
          />
        )}
      </Drawer>

      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={resourceRows.length > 1 ? false : true}
        columnHeaderHeight={resourceRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={resourceRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar
              entity_label={resource_label}
              disable_export={resourceRows.length === 0}
              handleCreate={handleCreateClick}
            />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={resource_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
      />
    </>
  );
};

export default PanelResourceCentre;
